import React from "react";

export function PrivacyScreen(): React.ReactElement {
  const sectionStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  };

  const headingStyle: React.CSSProperties = {
    fontSize: "16px",
    fontWeight: 700,
    color: "var(--color-graphite)",
    margin: "0",
    paddingTop: "6px",
  };

  const subHeadingStyle: React.CSSProperties = {
    fontSize: "14px",
    fontWeight: 600,
    color: "var(--color-graphite)",
    margin: "4px 0 0 0",
  };

  const paragraphStyle: React.CSSProperties = {
    margin: 0,
    fontSize: "14px",
    lineHeight: "1.6",
    color: "var(--color-graphite)",
  };

  const listStyle: React.CSSProperties = {
    margin: "4px 0 0 0",
    paddingLeft: "20px",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    fontSize: "14px",
    lineHeight: "1.6",
    color: "var(--color-graphite)",
  };

  const mailLinkStyle: React.CSSProperties = {
    color: "var(--color-graphite)",
    fontWeight: 600,
    textDecoration: "underline",
  };

  return (
    <div data-testid="privacy-screen" style={{ display: "flex", flexDirection: "column", gap: "16px", paddingBottom: "24px" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          padding: "4px 4px",
          minHeight: "44px",
          flexShrink: 0,
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: "22px",
            lineHeight: "26px",
            fontWeight: 700,
            color: "var(--color-graphite)",
            letterSpacing: "-0.02em",
          }}
        >
          Политика конфиденциальности
        </h1>
        <span
          style={{
            fontSize: "12px",
            color: "var(--color-slate)",
            marginTop: "4px",
            fontWeight: 500,
          }}
        >
          Редакция от 28 августа 2026 года
        </span>
      </div>

      {/* Content Card */}
      <div
        style={{
          backgroundColor: "var(--color-paper)",
          borderRadius: "var(--radius-card)",
          boxShadow: "var(--shadow-floating)",
          padding: "20px 18px",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
        }}
      >
        {/* Preamble */}
        <p style={paragraphStyle}>
          Настоящая Политика конфиденциальности описывает, какие данные обрабатываются при использовании сервиса <strong>Corgi Work</strong> (далее — «Сервис»), зачем они необходимы, как хранятся и в каких случаях могут передаваться сторонним поставщикам услуг.
        </p>
        <p style={{ ...paragraphStyle, fontWeight: 500 }}>
          Используя Telegram-бот Corgi Work или Telegram Mini App, Пользователь подтверждает, что ознакомился с настоящей Политикой конфиденциальности.
        </p>

        {/* 1. Основные принципы */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>1. Основные принципы</h2>
          <p style={paragraphStyle}>Corgi Work стремится собирать только те данные, которые необходимы для работы Сервиса.</p>
          <p style={paragraphStyle}>Мы не продаём персональные данные Пользователей и не используем их для сторонней рекламы.</p>
          <p style={paragraphStyle}>В настоящее время Corgi Work:</p>
          <ul style={listStyle}>
            <li>не использует рекламные трекеры;</li>
            <li>не использует системы продуктовой аналитики;</li>
            <li>не отслеживает переходы по ссылкам на вакансии;</li>
            <li>на веб-сайте с предварительного согласия Пользователя могут использоваться аналитические cookies (Яндекс Метрика) для подсчёта переходов;</li>
            <li>не использует localStorage или sessionStorage;</li>
            <li>не создаёт рекламные или маркетинговые профили;</li>
            <li>не отслеживает действия Пользователя на сторонних сайтах;</li>
            <li>не использует AI или внешние LLM-сервисы для персонального подбора вакансий.</li>
          </ul>
        </section>

        {/* 2. Какие данные мы сохраняем */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>2. Какие данные мы сохраняем</h2>

          <h3 style={subHeadingStyle}>2.1. Идентификаторы Telegram</h3>
          <p style={paragraphStyle}>Для работы Сервиса сохраняются:</p>
          <ul style={listStyle}>
            <li><strong>Telegram User ID</strong> — числовой идентификатор Пользователя в Telegram;</li>
            <li><strong>Chat ID</strong> — идентификатор личного диалога с ботом, необходимый для отправки сообщений.</li>
          </ul>
          <p style={paragraphStyle}>Corgi Work не сохраняет в своей пользовательской базе:</p>
          <ul style={listStyle}>
            <li>имя;</li>
            <li>фамилию;</li>
            <li>Telegram username;</li>
            <li>номер телефона;</li>
            <li>фотографию или аватар профиля.</li>
          </ul>
          <p style={paragraphStyle}>При взаимодействии с Telegram бот технически может получать данные, содержащиеся в Telegram Update или данных запуска Mini App. Они используются в объёме, необходимом для обработки запроса и проверки его подлинности.</p>

          <h3 style={subHeadingStyle}>2.2. Данные аккаунта</h3>
          <p style={paragraphStyle}>Для работы аккаунта сохраняются:</p>
          <ul style={listStyle}>
            <li>внутренний идентификатор Пользователя;</li>
            <li>дата первоначальной регистрации;</li>
            <li>дата начала пробного периода;</li>
            <li>информация о том, использован ли пробный период;</li>
            <li>технические отметки об отправленных уведомлениях, связанных с пробным периодом;</li>
            <li>отметка о невозможности доставки сообщений, если Пользователь заблокировал или остановил бота.</li>
          </ul>
          <p style={paragraphStyle}>Мы не ведём историю входов Пользователя и не сохраняем lastSeen или аналогичные данные о его активности.</p>

          <h3 style={subHeadingStyle}>2.3. Поисковые профили</h3>
          <p style={paragraphStyle}>Corgi Work сохраняет параметры, которые Пользователь самостоятельно указывает при настройке поиска вакансий. Они могут включать:</p>
          <ul style={listStyle}>
            <li>специализации;</li>
            <li>профессиональный уровень или грейд;</li>
            <li>языки вакансий;</li>
            <li>ключевые навыки;</li>
            <li>стоп-слова и исключающие фразы;</li>
            <li>дату последнего изменения профиля.</li>
          </ul>
          <p style={paragraphStyle}>Эти данные используются для поиска и фильтрации подходящих вакансий.</p>
          <p style={paragraphStyle}>Пользователю не следует вводить в поля навыков и исключений конфиденциальную личную информацию, не связанную с поиском работы.</p>
        </section>

        {/* 3. История доставки вакансий */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>3. История доставки вакансий</h2>
          <p style={paragraphStyle}>Чтобы не отправлять одну и ту же вакансию повторно, Corgi Work сохраняет техническую историю доставки. Она может содержать:</p>
          <ul style={listStyle}>
            <li>идентификатор Пользователя;</li>
            <li>идентификатор вакансии;</li>
            <li>время отправки;</li>
            <li>технические SHA-256-хэши текстов и ссылок вакансий;</li>
            <li>сведения, необходимые для обнаружения одинаковых вакансий из разных источников.</li>
          </ul>
          <p style={paragraphStyle}>Эта информация используется для работы системы дедупликации и не используется для отслеживания поведения Пользователя.</p>
        </section>

        {/* 4. Техническая информация о подборе вакансий */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>4. Техническая информация о подборе вакансий</h2>
          <p style={paragraphStyle}>Для диагностики и настройки системы фильтрации Corgi Work может временно сохранять технические записи о том, почему конкретная вакансия была признана соответствующей определённому поисковому профилю. Такие записи могут включать:</p>
          <ul style={listStyle}>
            <li>применённые правила фильтрации;</li>
            <li>результаты отдельных проверок;</li>
            <li>технические веса и оценки соответствия.</li>
          </ul>
          <p style={paragraphStyle}>Они используются для:</p>
          <ul style={listStyle}>
            <li>поиска ошибок;</li>
            <li>проверки корректности фильтрации;</li>
            <li>улучшения качества алгоритма;</li>
            <li>технической диагностики.</li>
          </ul>
          <p style={paragraphStyle}>Эта информация не используется для рекламы.</p>
        </section>

        {/* 5. Какие действия Пользователя мы не отслеживаем */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>5. Какие действия Пользователя мы не отслеживаем</h2>
          <p style={paragraphStyle}>Corgi Work не ведёт скрытого анализа поведения Пользователя. В частности, мы не сохраняем:</p>
          <ul style={listStyle}>
            <li>факт открытия конкретной вакансии;</li>
            <li>переходы по ссылкам;</li>
            <li>отклики на вакансии;</li>
            <li>историю просмотренных экранов Mini App;</li>
            <li>клики по элементам интерфейса;</li>
            <li>историю входов;</li>
            <li>историю сессий;</li>
            <li>координаты курсора или касаний;</li>
            <li>записи экрана;</li>
            <li>рекламные идентификаторы;</li>
            <li>browser fingerprints.</li>
          </ul>
          <p style={paragraphStyle}>Ссылки на вакансии ведут непосредственно на первоисточник или другой соответствующий адрес назначения. Corgi Work не использует промежуточные ссылки для отслеживания кликов.</p>
          <p style={paragraphStyle}>В Сервисе также отсутствует функциональность «Избранное» или история пользовательского скрытия отдельных вакансий, поэтому такие данные не собираются.</p>
        </section>

        {/* 6. Подписка и платежи */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>6. Подписка и платежи</h2>
          <p style={paragraphStyle}>Для управления платным доступом Corgi Work хранит только технические сведения о подписке и результатах платежей.</p>
          
          <h3 style={subHeadingStyle}>Данные подписки</h3>
          <p style={paragraphStyle}>Могут сохраняться:</p>
          <ul style={listStyle}>
            <li>идентификатор используемого платёжного провайдера;</li>
            <li>идентификатор подписки на стороне платёжного провайдера;</li>
            <li>статус подписки;</li>
            <li>даты начала и окончания оплаченного периода;</li>
            <li>информация об отключении автоматического продления.</li>
          </ul>

          <h3 style={subHeadingStyle}>История платежей</h3>
          <p style={paragraphStyle}>Могут сохраняться:</p>
          <ul style={listStyle}>
            <li>идентификатор транзакции;</li>
            <li>сумма;</li>
            <li>валюта;</li>
            <li>статус операции;</li>
            <li>дата и время платежа.</li>
          </ul>

          <h3 style={subHeadingStyle}>Платёжные сессии</h3>
          <p style={paragraphStyle}>Для направления Пользователя на оплату могут временно сохраняться:</p>
          <ul style={listStyle}>
            <li>идентификатор сформированного заказа;</li>
            <li>ссылка на защищённую страницу оплаты;</li>
            <li>статус заказа.</li>
          </ul>
          <p style={paragraphStyle}>Конкретный платёжный провайдер может быть заменён другим без изменения общей логики обработки данных.</p>
        </section>

        {/* 7. Банковские данные */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>7. Банковские данные</h2>
          <p style={paragraphStyle}>Corgi Work не получает и не хранит:</p>
          <ul style={listStyle}>
            <li>полный номер банковской карты;</li>
            <li>CVC/CVV;</li>
            <li>срок действия карты;</li>
            <li>PIN-код;</li>
            <li>банковские пароли;</li>
            <li>данные, используемые для подтверждения операции;</li>
            <li>имя держателя карты, если оно вводится непосредственно платёжному провайдеру.</li>
          </ul>
          <p style={paragraphStyle}>Эти сведения вводятся Пользователем непосредственно на стороне банка, платёжной системы или стороннего платёжного провайдера.</p>
          <p style={paragraphStyle}>Corgi Work получает только информацию, необходимую для определения результата операции и состояния подписки.</p>
        </section>

        {/* 8. Временные сообщения и очереди */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>8. Временные сообщения и очереди</h2>
          <p style={paragraphStyle}>Для надёжной работы Telegram-бота Corgi Work использует технические очереди. В них могут временно находиться:</p>
          <ul style={listStyle}>
            <li>вакансии, подготовленные к отправке;</li>
            <li>ответы на команды бота;</li>
            <li>входящие Telegram Updates;</li>
            <li>техническая информация, необходимая для повторной обработки сообщения в случае временного сбоя.</li>
          </ul>
          <p style={paragraphStyle}>Такие записи используются только для технической обработки и очищаются после выполнения соответствующей задачи согласно жизненному циклу очереди.</p>
        </section>

        {/* 9. Авторизация в Mini App */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>9. Авторизация в Mini App</h2>
          <p style={paragraphStyle}>Авторизация в Telegram Mini App построена без постоянного хранения пользовательской сессии на устройстве. Процесс работает следующим образом:</p>
          <ul style={listStyle}>
            <li>Telegram предоставляет Mini App подписанные данные запуска.</li>
            <li>Backend Corgi Work проверяет их подлинность.</li>
            <li>После успешной проверки создаётся временный токен авторизации.</li>
            <li>Он хранится только в оперативной памяти приложения во время текущего сеанса.</li>
            <li>После закрытия Mini App токен не сохраняется Corgi Work в браузерном хранилище.</li>
          </ul>
          <p style={paragraphStyle}>Corgi Work не использует для авторизации:</p>
          <ul style={listStyle}>
            <li>cookies;</li>
            <li>localStorage;</li>
            <li>sessionStorage.</li>
          </ul>
        </section>

        {/* 10. Аналитика */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>10. Аналитика</h2>
          <p style={paragraphStyle}>10.1. В Telegram Mini App и боте Corgi Work сторонняя пользовательская аналитика не используется.</p>
          <p style={paragraphStyle}>10.2. На публичном веб-сайте Corgi Work с предварительного согласия Пользователя может использоваться сервис веб-аналитики «Яндекс Метрика» исключительно для подсчёта общей посещаемости и количества переходов в Telegram-бот.</p>
          <p style={paragraphStyle}>10.3. В рамках аналитики:</p>
          <ul style={listStyle}>
            <li>не используются функции Вебвизора (запись действий экрана);</li>
            <li>не собираются персональные данные, email, Telegram ID или поисковые фильтры;</li>
            <li>Пользователь вправе в любой момент отозвать или изменить согласие на использование аналитических cookies через настройки на сайте.</li>
          </ul>
        </section>

        {/* 11. Сетевые и технические данные */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>11. Сетевые и технические данные</h2>
          <p style={paragraphStyle}>Corgi Work не ведёт в своей пользовательской базе историю IP-адресов Пользователей и не использует IP-адрес для построения пользовательских профилей.</p>
          <p style={paragraphStyle}>При этом передача данных через интернет неизбежно требует технической обработки сетевой информации инфраструктурными поставщиками. Например, при открытии Mini App или обращении к API серверы, CDN и средства сетевой защиты могут технически обрабатывать:</p>
          <ul style={listStyle}>
            <li>IP-адрес;</li>
            <li>дату и время запроса;</li>
            <li>тип запроса;</li>
            <li>технические HTTP-заголовки;</li>
            <li>информацию, необходимую для маршрутизации, защиты от атак и обеспечения стабильной работы.</li>
          </ul>
          <p style={paragraphStyle}>Такая обработка является частью работы интернет-инфраструктуры.</p>
        </section>

        {/* 12. Для чего используются данные */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>12. Для чего используются данные</h2>
          <p style={paragraphStyle}>Данные Corgi Work используются для следующих целей:</p>
          <ul style={listStyle}>
            <li><strong>Работа аккаунта</strong> — чтобы идентифицировать Пользователя внутри Сервиса и связать с ним настройки, пробный период и подписку.</li>
            <li><strong>Поиск вакансий</strong> — чтобы сохранять выбранные Пользователем критерии и находить вакансии, соответствующие этим критериям.</li>
            <li><strong>Доставка сообщений</strong> — чтобы отправлять Пользователю вакансии и служебные сообщения через Telegram.</li>
            <li><strong>Защита от повторов</strong> — чтобы одна и та же вакансия не отправлялась Пользователю многократно.</li>
            <li><strong>Управление подпиской</strong> — чтобы определять наличие платного доступа, учитывать успешные платежи, продления, отмены и возвраты.</li>
            <li><strong>Обеспечение стабильности</strong> — чтобы обрабатывать очереди сообщений, диагностировать ошибки и восстанавливать работу после временных сбоев.</li>
            <li><strong>Улучшение фильтрации</strong> — чтобы проверять качество работы алгоритма и исправлять случаи неправильного сопоставления вакансий.</li>
            <li><strong>Поддержка</strong> — чтобы отвечать на обращения Пользователей и решать технические или платёжные вопросы.</li>
            <li><strong>Исполнение обязательных требований</strong> — в отдельных случаях информация может использоваться или сохраняться, если этого требует применимое законодательство или защита законных прав Сервиса и Пользователей.</li>
          </ul>
        </section>

        {/* 13. Автоматический подбор вакансий */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>13. Автоматический подбор вакансий</h2>
          <p style={paragraphStyle}>Corgi Work автоматически сравнивает информацию о вакансии с критериями, которые Пользователь самостоятельно указал в поисковом профиле.</p>
          <p style={paragraphStyle}>В настоящее время персональный подбор не выполняется внешними AI или LLM-сервисами.</p>
          <p style={paragraphStyle}>Алгоритм определяет только то, следует ли отправить конкретную вакансию Пользователю. Он не:</p>
          <ul style={listStyle}>
            <li>принимает решение о найме;</li>
            <li>отправляет отклики за Пользователя;</li>
            <li>принимает решения от имени работодателя;</li>
            <li>передаёт резюме работодателю;</li>
            <li>определяет возможность трудоустройства;</li>
            <li>создаёт обязательства между Пользователем и работодателем.</li>
          </ul>
        </section>

        {/* 14. Сторонние поставщики услуг */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>14. Сторонние поставщики услуг</h2>
          <p style={paragraphStyle}>Для работы Corgi Work используется техническая инфраструктура третьих лиц. Данные могут обрабатываться ограниченными категориями поставщиков в объёме, необходимом для соответствующей функции.</p>

          <h3 style={subHeadingStyle}>Telegram</h3>
          <p style={paragraphStyle}>Используется для:</p>
          <ul style={listStyle}>
            <li>запуска бота;</li>
            <li>получения команд;</li>
            <li>идентификации Пользователя;</li>
            <li>запуска Mini App;</li>
            <li>доставки вакансий и служебных сообщений.</li>
          </ul>
          <p style={paragraphStyle}>Telegram самостоятельно обрабатывает данные в рамках своей платформы.</p>

          <h3 style={subHeadingStyle}>Поставщики облачной инфраструктуры</h3>
          <p style={paragraphStyle}>Используются для:</p>
          <ul style={listStyle}>
            <li>работы backend;</li>
            <li>размещения базы данных;</li>
            <li>выполнения фоновых задач;</li>
            <li>размещения Mini App;</li>
            <li>доставки статических файлов;</li>
            <li>маршрутизации сетевого трафика.</li>
          </ul>

          <h3 style={subHeadingStyle}>Поставщики платёжных услуг</h3>
          <p style={paragraphStyle}>Используются для:</p>
          <ul style={listStyle}>
            <li>проведения оплаты;</li>
            <li>управления рекуррентными платежами;</li>
            <li>подтверждения статуса транзакции;</li>
            <li>обработки отмен и возвратов.</li>
          </ul>
          <p style={paragraphStyle}>Corgi Work не получает от них полные реквизиты банковской карты.</p>

          <h3 style={subHeadingStyle}>Поставщики технического мониторинга</h3>
          <p style={paragraphStyle}>Могут использоваться для:</p>
          <ul style={listStyle}>
            <li>обнаружения сбоев;</li>
            <li>мониторинга состояния серверов;</li>
            <li>получения технических логов;</li>
            <li>контроля работоспособности фоновых процессов.</li>
          </ul>
          <p style={paragraphStyle}>Такие системы не используются Corgi Work как инструмент пользовательской аналитики или рекламного отслеживания.</p>
        </section>

        {/* 15. Передача данных третьим лицам */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>15. Передача данных третьим лицам</h2>
          <p style={paragraphStyle}>Corgi Work не продаёт персональные данные и не предоставляет базы Пользователей рекламодателям, работодателям или рекрутерам. Данные могут передаваться только:</p>
          <ul style={listStyle}>
            <li>поставщикам инфраструктуры, необходимой для работы Сервиса;</li>
            <li>платёжным и финансовым сервисам в связи с оплатой;</li>
            <li>Telegram в рамках использования его платформы;</li>
            <li>поставщикам технической безопасности и мониторинга;</li>
            <li>компетентным органам, если предоставление информации обязательно в соответствии с применимыми требованиями.</li>
          </ul>
          <p style={paragraphStyle}>Передача ограничивается объёмом, необходимым для соответствующей цели.</p>
        </section>

        {/* 16. Обработка данных в разных странах */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>16. Обработка данных в разных странах</h2>
          <p style={paragraphStyle}>Corgi Work использует глобальные интернет-сервисы и поставщиков инфраструктуры. Поэтому отдельные технические операции могут выполняться на серверах или инфраструктуре, расположенных в разных странах.</p>
          <p style={paragraphStyle}>При выборе поставщиков Corgi Work стремится использовать сервисы, применяющие общепринятые меры защиты данных и защищённую передачу информации.</p>
          <p style={paragraphStyle}>Факт использования глобальной инфраструктуры не означает, что Corgi Work предоставляет Пользовательские данные третьим лицам для их собственных рекламных или маркетинговых целей.</p>
        </section>

        {/* 17. Технические журналы и мониторинг */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>17. Технические журналы и мониторинг</h2>
          <p style={paragraphStyle}>Сервис может создавать технические журналы, необходимые для диагностики ошибок и контроля работоспособности. Corgi Work не использует их как историю поведения Пользователя.</p>
          <p style={paragraphStyle}>Мы не стремимся записывать в технические логи содержимое пользовательских поисковых профилей или иные персональные сведения без необходимости.</p>
          <p style={paragraphStyle}>При этом отдельные технические идентификаторы могут появляться в серверных журналах, если это необходимо для диагностики конкретной операции или ошибки.</p>
        </section>

        {/* 18. Срок хранения данных */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>18. Срок хранения данных</h2>
          <p style={paragraphStyle}>Corgi Work хранит данные не дольше, чем это необходимо для целей, для которых они используются.</p>
          <ul style={listStyle}>
            <li><strong>Данные аккаунта</strong> — Telegram User ID, Chat ID и другие основные данные аккаунта хранятся, пока аккаунт существует и необходим для предоставления Сервиса.</li>
            <li><strong>Поисковые профили</strong> — хранятся до их удаления Пользователем либо удаления аккаунта.</li>
            <li><strong>История доставок</strong> — хранится столько, сколько необходимо для предотвращения повторной отправки вакансий и корректной работы Сервиса.</li>
            <li><strong>Технические решения фильтрации</strong> — хранятся ограниченное время, необходимое для диагностики и настройки алгоритмов.</li>
            <li><strong>Очереди сообщений</strong> — содержат информацию только в течение периода, необходимого для обработки сообщения и последующей технической очистки.</li>
            <li><strong>Платёжные данные</strong> — техническая история подписки и платежей хранится столько, сколько необходимо для управления платным доступом, решения вопросов по платежам, возвратам и спорам.</li>
            <li><strong>Обращения в поддержку</strong> — переписка может храниться в течение периода, необходимого для рассмотрения обращения и решения связанного с ним вопроса.</li>
          </ul>
          <p style={paragraphStyle}>Отдельная информация может храниться дольше, если её сохранение необходимо в силу обязательных требований либо для защиты от претензий и злоупотреблений.</p>
        </section>

        {/* 19. Обращения в поддержку */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>19. Обращения в поддержку</h2>
          <p style={paragraphStyle}>
            Если Пользователь пишет на{" "}
            <a href="mailto:corgiwork@proton.me" style={mailLinkStyle}>corgiwork@proton.me</a>, мы получаем сведения, которые он самостоятельно отправил в своём обращении. Они могут включать:
          </p>
          <ul style={listStyle}>
            <li>email-адрес отправителя;</li>
            <li>содержание письма;</li>
            <li>вложения;</li>
            <li>технические данные электронной переписки.</li>
          </ul>
          <p style={paragraphStyle}>Такие данные используются для рассмотрения обращения и решения соответствующего вопроса.</p>
          <p style={paragraphStyle}>Пользователю не следует отправлять в поддержку данные банковских карт, пароли, коды подтверждения или иные секретные сведения.</p>
        </section>

        {/* 20. Безопасность */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>20. Безопасность</h2>
          <p style={paragraphStyle}>Corgi Work принимает разумные технические и организационные меры для защиты информации от несанкционированного доступа, изменения, утраты или раскрытия. В частности, используются:</p>
          <ul style={listStyle}>
            <li>защищённое соединение HTTPS;</li>
            <li>проверка подлинности данных Telegram;</li>
            <li>временная авторизация Mini App;</li>
            <li>отсутствие постоянного хранения токена на устройстве;</li>
            <li>минимизация сохраняемых Telegram-данных;</li>
            <li>отсутствие хранения полных реквизитов банковских карт;</li>
            <li>управляемая серверная инфраструктура;</li>
            <li>контроль доступа к production-системам;</li>
            <li>технический мониторинг состояния Сервиса.</li>
          </ul>
          <p style={paragraphStyle}>При этом ни один интернет-сервис не может гарантировать абсолютную защиту от всех возможных угроз.</p>
        </section>

        {/* 21. Удаление аккаунта */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>21. Удаление аккаунта</h2>
          <p style={paragraphStyle}>
            Пользователь может запросить полное удаление своего аккаунта Corgi Work. Для этого необходимо написать:{" "}
            <a href="mailto:corgiwork@proton.me" style={mailLinkStyle}>corgiwork@proton.me</a>
          </p>
          <p style={paragraphStyle}>Чтобы предотвратить удаление чужого аккаунта, Corgi Work может попросить подтвердить контроль над соответствующим Telegram-аккаунтом.</p>
          <p style={paragraphStyle}>После подтверждения запроса основная запись Пользователя удаляется из активной базы данных. Вместе с ней удаляются связанные:</p>
          <ul style={listStyle}>
            <li>Telegram User ID;</li>
            <li>Chat ID;</li>
            <li>поисковые профили;</li>
            <li>грейды;</li>
            <li>навыки;</li>
            <li>исключения и стоп-слова;</li>
            <li>история доставок;</li>
            <li>технические fingerprints;</li>
            <li>технические записи фильтрации;</li>
            <li>подписка;</li>
            <li>платёжные записи в базе Corgi Work;</li>
            <li>платёжные сессии;</li>
            <li>ожидающие отправки сообщения.</li>
          </ul>
          <p style={paragraphStyle}>После удаления аккаунта повторный запуск Corgi Work может привести к созданию нового пустого аккаунта.</p>
        </section>

        {/* 22. Ограничения удаления */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>22. Ограничения удаления</h2>
          <p style={paragraphStyle}>Удаление данных из активной базы Corgi Work не обязательно означает мгновенное удаление информации:</p>
          <ul style={listStyle}>
            <li>из резервных копий до их штатной перезаписи;</li>
            <li>из технических журналов до истечения срока их хранения;</li>
            <li>из систем независимых сторонних поставщиков;</li>
            <li>из данных, которые необходимо сохранить в соответствии с обязательными требованиями;</li>
            <li>из информации, необходимой для защиты законных требований и разрешения споров.</li>
          </ul>
          <p style={paragraphStyle}>Corgi Work не может удалить данные непосредственно из независимых систем Telegram, банка или платёжного провайдера. Пользователь взаимодействует с такими сервисами согласно их собственным правилам.</p>
        </section>

        {/* 23. Права Пользователя */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>23. Права Пользователя</h2>
          <p style={paragraphStyle}>Пользователь может обратиться к Corgi Work с запросом:</p>
          <ul style={listStyle}>
            <li>сообщить, какие данные о нём хранятся;</li>
            <li>исправить неточные данные;</li>
            <li>удалить аккаунт и связанные данные;</li>
            <li>получить разъяснение о том, для чего используется определённая информация;</li>
            <li>прекратить определённую обработку, если такая возможность предусмотрена применимыми правилами.</li>
          </ul>
          <p style={paragraphStyle}>
            Запрос можно направить на:{" "}
            <a href="mailto:corgiwork@proton.me" style={mailLinkStyle}>corgiwork@proton.me</a>
          </p>
          <p style={paragraphStyle}>Для защиты данных Corgi Work может запросить подтверждение того, что обращение действительно исходит от владельца соответствующего аккаунта.</p>
          <p style={paragraphStyle}>Права Пользователя также сохраняются в объёме, предусмотренном применимым законодательством.</p>
        </section>

        {/* 24. Данные работодателям и рекрутерам */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>24. Данные работодателям и рекрутерам</h2>
          <p style={paragraphStyle}>Corgi Work не является сервисом автоматического отклика. Мы:</p>
          <ul style={listStyle}>
            <li>не отправляем резюме Пользователя;</li>
            <li>не передаём его Telegram ID работодателям;</li>
            <li>не передаём поисковые профили рекрутерам;</li>
            <li>не сообщаем работодателям, какие вакансии были отправлены Пользователю;</li>
            <li>не отправляем отклики от имени Пользователя.</li>
          </ul>
          <p style={paragraphStyle}>Дальнейшее взаимодействие с работодателем начинается только по инициативе самого Пользователя за пределами Corgi Work.</p>
        </section>

        {/* 25. Сторонние ссылки */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>25. Сторонние ссылки</h2>
          <p style={paragraphStyle}>Corgi Work содержит ссылки на внешние ресурсы, например:</p>
          <ul style={listStyle}>
            <li>сайты работодателей;</li>
            <li>сайты с вакансиями;</li>
            <li>Telegram-каналы;</li>
            <li>email-адреса;</li>
            <li>другие платформы.</li>
          </ul>
          <p style={paragraphStyle}>После перехода на такой ресурс обработка данных регулируется правилами соответствующего владельца сайта или платформы. Corgi Work не контролирует обработку данных сторонними ресурсами.</p>
        </section>

        {/* 26. Изменение Политики */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>26. Изменение Политики</h2>
          <p style={paragraphStyle}>Настоящая Политика может обновляться в связи с:</p>
          <ul style={listStyle}>
            <li>изменением функций Corgi Work;</li>
            <li>подключением новых технологий;</li>
            <li>заменой инфраструктурных или платёжных поставщиков;</li>
            <li>изменением способов обработки данных;</li>
            <li>изменением обязательных требований.</li>
          </ul>
          <p style={paragraphStyle}>Актуальная редакция размещается внутри Сервиса или по доступной из него ссылке.</p>
          <p style={paragraphStyle}>При существенных изменениях Corgi Work может дополнительно уведомить Пользователей через Telegram или Mini App.</p>
          <p style={paragraphStyle}>Дата текущей редакции указывается в начале документа.</p>
        </section>

        {/* 27. Контакты */}
        <section style={sectionStyle}>
          <h2 style={headingStyle}>27. Контакты</h2>
          <p style={paragraphStyle}>По вопросам конфиденциальности, удаления аккаунта или обработки данных можно обращаться:</p>
          <p style={{ ...paragraphStyle, fontWeight: 600 }}>Corgi Work</p>
          <p style={paragraphStyle}>
            Email: <a href="mailto:corgiwork@proton.me" style={mailLinkStyle}>corgiwork@proton.me</a>
          </p>
        </section>
      </div>
    </div>
  );
}

export default PrivacyScreen;
